import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import useAuthStore from '@/store/authStore';
import apiClient from '@/lib/api';
import { Shield, Monitor, Link, Code, Activity, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DashboardOverview = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    sessions: 0,
    connectedApps: 0,
    developerApps: 0,
    twoFactorEnabled: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [sessionsRes, appsRes] = await Promise.all([
          apiClient.get('/sessions'),
          apiClient.get('/oauth/authorizations')
        ]);
        let developerApps = 0;
        if (user?.is_admin) {
          try {
            const devAppsRes = await apiClient.get('/developer/apps');
            developerApps = devAppsRes.data.length;
          } catch (_) {}
        }

        setStats({
          sessions: sessionsRes.data.length,
          connectedApps: appsRes.data.length,
          developerApps,
          twoFactorEnabled: user?.totp_enabled || false
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  const quickActions = [
    {
      title: 'Security Center',
      description: 'Manage 2FA and security settings',
      icon: Shield,
      onClick: () => navigate('/dashboard/security'),
      testId: 'quick-action-security'
    },
    {
      title: 'Active Sessions',
      description: 'View and manage your sessions',
      icon: Monitor,
      onClick: () => navigate('/dashboard/sessions'),
      testId: 'quick-action-sessions'
    },
    {
      title: 'Connected Apps',
      description: 'Manage connected applications',
      icon: Link,
      onClick: () => navigate('/dashboard/apps'),
      testId: 'quick-action-apps'
    },
    ...(user?.is_admin ? [{
      title: 'Developer Portal',
      description: 'Create and manage OAuth apps',
      icon: Code,
      onClick: () => navigate('/dashboard/developer'),
      testId: 'quick-action-developer'
    }] : [])
  ];

  return (
    <div data-testid="dashboard-overview" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Welcome back, {user?.name}</h2>
        <p className="text-zinc-600 dark:text-zinc-400 mt-1">
          Manage your LostDrkC Account and connected services
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-sessions">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Active Sessions
                </p>
                <p className="text-2xl font-bold mt-1">{stats.sessions}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                <Monitor className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-connected-apps">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Connected Apps
                </p>
                <p className="text-2xl font-bold mt-1">{stats.connectedApps}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <Link className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-developer-apps" className={user?.is_admin ? '' : 'hidden'}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Developer Apps
                </p>
                <p className="text-2xl font-bold mt-1">{stats.developerApps}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <Code className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-2fa">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Two-Factor Auth
                </p>
                <p className="text-2xl font-bold mt-1">
                  {stats.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                stats.twoFactorEnabled 
                  ? 'bg-green-50 dark:bg-green-900/20' 
                  : 'bg-orange-50 dark:bg-orange-900/20'
              }`}>
                {stats.twoFactorEnabled ? (
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                ) : (
                  <Shield className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Alert */}
      {!stats.twoFactorEnabled && (
        <Card className="border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-900/10" data-testid="security-alert">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Shield className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 dark:text-orange-200">
                  Enable Two-Factor Authentication
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                  Add an extra layer of security to your account. Protect your LostDrkC Account from unauthorized access.
                </p>
                <Button
                  variant="outline"
                  className="mt-3 border-orange-300 dark:border-orange-700"
                  onClick={() => navigate('/dashboard/security')}
                  data-testid="enable-2fa-button"
                >
                  Enable 2FA
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action, index) => (
            <Card
              key={index}
              className="cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors duration-200"
              onClick={action.onClick}
              data-testid={action.testId}
            >
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                      <action.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{action.title}</h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                      {action.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
